/**
 * Simple Smarter buy section enhancements (sections/ss-buy.liquid).
 *
 * - Gallery: thumbnails, swipe, and a native <dialog> lightbox.
 * - Quantity +/- buttons and variant select (price + availability).
 * - AJAX add-to-cart that dispatches the theme's standard CartLinesUpdateEvent so the
 *   cart drawer and cart icon refresh exactly as they do for the theme's own product form.
 *   Without JavaScript the form still posts to /cart/add.
 * - Mobile sticky Add to Cart bar, shown while the purchase panel is off screen.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = '[data-ss-buy]';

/** @param {HTMLElement} root */
function initGallery(root) {
  const gallery = root.querySelector('[data-ss-gallery]');
  if (!gallery) return;

  const slides = /** @type {HTMLButtonElement[]} */ ([...gallery.querySelectorAll('[data-ss-slide]')]);
  const thumbs = /** @type {HTMLButtonElement[]} */ ([...gallery.querySelectorAll('[data-ss-thumb]')]);
  const dialog = /** @type {HTMLDialogElement | null} */ (gallery.querySelector('[data-ss-lightbox]'));
  const lightboxImg = /** @type {HTMLImageElement | null} */ (gallery.querySelector('[data-ss-lightbox-img]'));
  if (slides.length === 0) return;

  let index = 0;

  const syncLightbox = () => {
    const slide = slides[index];
    if (!lightboxImg || !slide) return;
    lightboxImg.src = slide.dataset.ssFull || '';
    lightboxImg.alt = slide.dataset.ssAlt || '';
  };

  const caption = gallery.querySelector('[data-ss-caption]');

  /** @param {number} next */
  const show = (next) => {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      slide.hidden = i !== index;
    });
    if (caption) caption.textContent = slides[index]?.dataset.ssCaption || '';
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
    if (dialog?.open) syncLightbox();
  };

  for (const thumb of thumbs) {
    thumb.addEventListener('click', () => show(Number(thumb.dataset.ssThumb) || 0));
  }

  for (const slide of slides) {
    slide.addEventListener('click', () => {
      if (!dialog || typeof dialog.showModal !== 'function') return;
      syncLightbox();
      dialog.showModal();
    });
  }

  gallery.querySelector('[data-ss-close]')?.addEventListener('click', () => dialog?.close());
  gallery.querySelector('[data-ss-prev]')?.addEventListener('click', () => show(index - 1));
  gallery.querySelector('[data-ss-next]')?.addEventListener('click', () => show(index + 1));

  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  dialog?.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') show(index + 1);
    if (event.key === 'ArrowLeft') show(index - 1);
  });

  dialog?.addEventListener('close', () => slides[index]?.focus({ preventScroll: true }));

  const stage = gallery.querySelector('.ss-gallery__stage');
  /** @type {number | null} */
  let startX = null;
  stage?.addEventListener('touchstart', (event) => {
    startX = /** @type {TouchEvent} */ (event).touches[0]?.clientX ?? null;
  }, { passive: true });
  stage?.addEventListener('touchend', (event) => {
    if (startX === null) return;
    const endX = /** @type {TouchEvent} */ (event).changedTouches[0]?.clientX ?? startX;
    const delta = endX - startX;
    startX = null;
    if (Math.abs(delta) > 40 && slides.length > 1) show(index + (delta < 0 ? 1 : -1));
  }, { passive: true });
}

/** @param {HTMLElement} root */
function initQuantity(root) {
  const input = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-ss-qty-input]'));
  if (!input) return;

  const clamp = (/** @type {number} */ value) => Math.min(Number(input.max) || 99, Math.max(Number(input.min) || 1, value));

  for (const button of root.querySelectorAll('[data-ss-qty]')) {
    button.addEventListener('click', () => {
      const step = Number(/** @type {HTMLElement} */ (button).dataset.ssQty) || 0;
      input.value = String(clamp((parseInt(input.value, 10) || 1) + step));
    });
  }

  input.addEventListener('change', () => {
    input.value = String(clamp(parseInt(input.value, 10) || 1));
  });
}

/** @param {HTMLElement} root @param {boolean} available */
function setAvailability(root, available) {
  const label = available ? root.dataset.textAdd : root.dataset.textSoldOut;
  const addLabel = root.querySelector('[data-ss-add-label]');
  if (addLabel && label) addLabel.textContent = label;

  for (const button of root.querySelectorAll('[data-ss-add], [data-ss-sticky-add]')) {
    /** @type {HTMLButtonElement} */ (button).disabled = !available;
  }

  const sticky = root.querySelector('[data-ss-sticky-add]');
  if (sticky && label) sticky.textContent = label;
}

/** @param {HTMLElement} root */
function initVariantSelect(root) {
  const select = /** @type {HTMLSelectElement | null} */ (root.querySelector('[data-ss-variant-select]'));
  if (!select) return;

  select.addEventListener('change', () => {
    const option = select.selectedOptions[0];
    if (!option) return;
    for (const price of root.querySelectorAll('[data-ss-price]')) {
      price.textContent = option.dataset.price || price.textContent;
    }
    setAvailability(root, option.dataset.available === 'true');
  });
}

/** @param {HTMLElement} root */
function initForm(root) {
  const form = /** @type {HTMLFormElement | null} */ (root.querySelector('form[data-ss-form]'));
  const status = root.querySelector('[data-ss-status]');
  const addLabel = root.querySelector('[data-ss-add-label]');
  if (!form) return;

  const buttons = /** @type {HTMLButtonElement[]} */ ([...root.querySelectorAll('[data-ss-add], [data-ss-sticky-add]')]);
  const routes = /** @type {any} */ (window).Theme?.routes ?? {};
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = `${routes.cart_url || '/cart'}.js`;
  let busy = false;

  /** @param {string} message @param {'success' | 'error'} state */
  const announce = (message, state) => {
    if (!status) return;
    status.textContent = message;
    status.setAttribute('data-state', state);
  };

  /** @param {boolean} value */
  const setBusy = (value) => {
    busy = value;
    for (const button of buttons) button.setAttribute('aria-busy', String(value));
    if (addLabel) {
      addLabel.textContent = value ? root.dataset.textAdding || '' : root.dataset.textAdd || '';
    }
  };

  const sticky = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-ss-sticky-add]'));

  /** Briefly confirms on the sticky bar, which is the only purchase control visible on mobile. */
  const confirmSticky = () => {
    if (!sticky) return;
    const original = sticky.textContent;
    sticky.textContent = root.dataset.textAddedShort || 'Added';
    window.setTimeout(() => {
      sticky.textContent = original;
    }, 2000);
  };

  /**
   * Makes sure the shopper can see their cart after adding. The theme opens its drawer itself when
   * "auto open" is enabled; if it hasn't opened shortly after, open it here. Without a drawer
   * (cart page mode), go to the cart page.
   */
  const revealCart = () => {
    const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
    if (!drawer) {
      window.location.href = routes.cart_url || '/cart';
      return;
    }
    window.setTimeout(() => {
      const isOpen = drawer.isOpen ?? drawer.hasAttribute('open');
      if (!isOpen && typeof drawer.open === 'function') drawer.open();
    }, 450);
  };

  const fetchCart = async () => {
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    if (buttons.some((button) => button.disabled)) return;

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    const variantId = String(formData.get('id') || '');

    setBusy(true);
    announce('', 'success');

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        credentials: 'same-origin',
        body: formData,
      });
      const data = await response.json();
      const cart = await fetchCart();

      if (!response.ok || data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'ss-buy', itemCount: quantity },
        });
        form.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        announce(data.description || data.message || root.dataset.textError || '', 'error');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'ss-buy',
          itemCount: quantity,
          sections: data.sections,
        },
      });
      announce(root.dataset.textAdded || '', 'success');
      confirmSticky();
      revealCart();
    } catch (error) {
      deferred.reject(error);
      console.error('[ss-buy] Add to cart failed', error);
      announce(root.dataset.textError || '', 'error');
    } finally {
      setBusy(false);
    }
  });
}

/** @param {HTMLElement} root */
function initSticky(root) {
  const bar = /** @type {HTMLElement | null} */ (root.querySelector('[data-ss-sticky]'));
  const panel = root.querySelector('[data-ss-buy-panel]');
  if (!bar || !panel || !('IntersectionObserver' in window)) return;

  const mobile = window.matchMedia('(max-width: 749px)');
  let panelVisible = false;

  const update = () => {
    const show = mobile.matches && !panelVisible && window.scrollY > window.innerHeight * 0.6;
    if (bar.hidden === !show) return;
    bar.hidden = !show;
    document.body.style.paddingBottom = show ? `${bar.offsetHeight}px` : '';
  };

  new IntersectionObserver(([entry]) => {
    panelVisible = Boolean(entry?.isIntersecting);
    update();
  }).observe(panel);

  window.addEventListener('scroll', update, { passive: true });
  mobile.addEventListener('change', update);
  update();
}

/** @param {ParentNode} scope */
function init(scope) {
  for (const element of scope.querySelectorAll(ROOT)) {
    const root = /** @type {HTMLElement} */ (element);
    if (root.dataset.ssReady === 'true') continue;
    root.dataset.ssReady = 'true';
    initGallery(root);
    initQuantity(root);
    initVariantSelect(root);
    initForm(root);
    initSticky(root);
  }
}

init(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
